import { action, makeObservable, observable } from 'mobx';

/**
 * Service for managing accessibility announcements and screen reader interactions
 */
class AccessibilityService {
  @observable
  feedbackAnnouncement = '';

  @observable
  announcementId = 0;

  // Track last announcement to prevent duplicates
  lastAnnouncement = '';
  lastAnnouncementTime = 0;

  constructor() {
    makeObservable(this);
  }

  /**
   * Announces feedback to screen readers immediately
   * @param {string} message - The feedback message to announce
   * @param {string} type - The type of feedback ('correct', 'incorrect', 'partial', etc.)
   */
  @action
  announceFeedback = (message, type = 'feedback') => {
    if (!message || typeof message !== 'string') {
      return;
    }
    
    // Strip HTML tags for screen reader announcement
    const cleanMessage = this.stripHtmlTags(message);
    
    // Prevent duplicate announcements within 500ms
    const now = Date.now();
    if (this.lastAnnouncement === cleanMessage && (now - this.lastAnnouncementTime) < 500) {
      return;
    }
    
    // Update last announcement tracking
    this.lastAnnouncement = cleanMessage;
    this.lastAnnouncementTime = now;
    
    // Create a unique announcement to ensure it triggers
    this.announcementId += 1;
    this.feedbackAnnouncement = `${cleanMessage}`;
    
    // Clear the announcement after a delay to allow screen readers to pick it up
    setTimeout(() => {
      this.clearFeedbackAnnouncement();
    }, 1000);
  };

  /**
   * Announces question submission result
   * @param {boolean} isCorrect - Whether the answer was correct
   * @param {string} feedbackText - Additional feedback text
   */
  @action
  announceSubmissionResult = (isCorrect, feedbackText = '') => {
    let message = '';
    
    if (isCorrect) {
      message = 'Correct!';
    } else {
      message = 'Incorrect.';
    }
    
    if (feedbackText) {
      const cleanFeedback = this.stripHtmlTags(feedbackText);
      message += ` ${cleanFeedback}`;
    }
    
    this.announceFeedback(message, isCorrect ? 'correct' : 'incorrect');
  };

  /**
   * Announces that answers are being shown
   */
  @action
  announceShowAnswers = () => {
    this.announceFeedback('Showing correct answers.', 'showAnswers');
  };

  /**
   * Announces that a hint is being shown
   */
  @action
  announceShowHint = () => {
    this.announceFeedback('Hint displayed.', 'hint');
  };

  /**
   * Announces that a solution is being shown
   */
  @action
  announceShowSolution = () => {
    this.announceFeedback('Solution displayed.', 'solution');
  };

  /**
   * Clears the current feedback announcement
   */
  @action
  clearFeedbackAnnouncement = () => {
    this.feedbackAnnouncement = '';
  };

  /**
   * Strips HTML tags from a string for screen reader consumption
   * @param {string} htmlString - The HTML string to clean
   * @returns {string} - Clean text without HTML tags
   */
  stripHtmlTags = (htmlString) => {
    if (!htmlString || typeof htmlString !== 'string') {
      return '';
    }
    
    // Create a temporary div to parse HTML and extract text
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;
    
    // Get text content and normalize whitespace
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    return textContent.replace(/\s+/g, ' ').trim();
  };

  /**
   * Announces a generic message
   * @param {string} message - The message to announce
   */
  @action
  announceMessage = (message) => {
    this.announceFeedback(message, 'generic');
  };

  /**
   * Announces character/word count changes aggressively (always announces)
   * @param {string} message - The count message to announce
   */
  @action
  announceCountChange = (message) => {
    if (!message || typeof message !== 'string') {
      return;
    }
    
    // Strip HTML tags for screen reader announcement
    const cleanMessage = this.stripHtmlTags(message);
    
    // Always announce count changes (no duplicate prevention)
    this.announcementId += 1;
    this.feedbackAnnouncement = `${cleanMessage}`;
    
    // Clear the announcement after a delay to allow screen readers to pick it up
    setTimeout(() => {
      this.clearFeedbackAnnouncement();
    }, 1000);
  };
}

export default new AccessibilityService(); 